import { useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface ShareOptions {
  message: string
  url?: string
  title?: string
}

export function useDeviceShare() {
  const { t } = useTranslation()

  const share = useCallback(
    async ({ message, url, title }: ShareOptions) => {
      try {
        // Check Web Share API support
        if (navigator.share) {
          await navigator.share({
            title: title || t('translation:shareMyProfile', 'Share My Profile'),
            text: message,
            url: url,
          })
          return
        }

        // Fallback: copy to clipboard
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(message)
          toast.success(
            t('translation:copiedToClipboard', 'Copied to clipboard'),
          )
          return
        }

        // Last fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = message
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
          document.execCommand('copy')
          toast.success(
            t('translation:copiedToClipboard', 'Copied to clipboard'),
          )
        } catch (err) {
          toast.error(t('translation:failedToCopy', 'Failed to copy'))
        } finally {
          document.body.removeChild(textArea)
        }
      } catch (error) {
        // Silently handle share errors without showing notification
      }
    },
    [t],
  )

  const copy = useCallback(
    async (text: string, toastMessage?: string) => {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text)
          toast.success(
            toastMessage ||
              t('translation:copiedToClipboard', 'Copied to clipboard'),
          )
          return
        }

        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
          document.execCommand('copy')
          toast.success(
            toastMessage ||
              t('translation:copiedToClipboard', 'Copied to clipboard'),
          )
        } catch (err) {
          toast.error(t('translation:failedToCopy', 'Failed to copy'))
        } finally {
          document.body.removeChild(textArea)
        }
      } catch (error) {
        toast.error(t('translation:failedToCopy', 'Failed to copy'))
      }
    },
    [t],
  )

  return { share, copy }
}
